// Pure fuel-rate helpers. Kept out of ./utils.js because that module imports the tariff type
// files, which reach store.js -> hooks -> App and drag Firebase and the SSO client into any consumer.

// Merges a partial edit into a fuel bucket. Each cell edits one field, so an absent `rate`
// (max-cell edit) or `max` (rate-cell edit) must leave the stored value alone — a bare
// `rate` after the spread would wipe the configured surcharge with undefined.
export const mergeFuelBucket = (bucket, { rate, max = null }) => ({
    ...(bucket || {}),
    ...(rate !== undefined ? { rate } : {}),
    ...(max !== null ? { max } : {}),
});

// Builds the fuel-price-indexed buckets for the "Add Calculated FSC" helper: evenly-sized
// `min -> max` bands from startRate up, each stepping the surcharge by surchargeInterval.
// The last bucket's max is the open-ended 'infinity' tail. Shared by all three fuel modes.
// @param isPercentage - store the rate as a fraction (Modifier) instead of whole dollars
export const generateFuelBuckets = ({
    startRate,
    endRate,
    surchargeInterval,
    rateInterval,
    startingSurcharge,
    isPercentage = false,
}) => {
    // Guard only — FuelModal blocks submit unless rateInterval > 0. Without it a zero
    // interval yields Infinity intervals and hangs the loop.
    if (!(rateInterval > 0)) return [];

    // Round the quotient before ceiling: (2.1 - 2.0) / 0.1 is 1.0000000000000009 in IEEE-754,
    // which would ceil to a spurious extra band. A genuine trailing remainder is never within
    // 1e-8 of an interval, so this only cancels float error.
    const numIntervals =
        endRate === startRate ? 1 : Math.max(1, Math.ceil(Number(((endRate - startRate) / rateInterval).toFixed(8))));
    const buckets = [];
    for (let i = 0; i < numIntervals; i++) {
        const min = Number((startRate + i * rateInterval).toFixed(2));
        const max = i === numIntervals - 1 ? 'infinity' : Number((startRate + (i + 1) * rateInterval).toFixed(2));
        const surcharge = startingSurcharge + i * surchargeInterval;
        buckets.push({
            min,
            max,
            rate: isPercentage ? surcharge / 100 : surcharge,
            bucket: i,
        });
    }
    return buckets;
};
